/**
 * Hook for managing video generation with Supabase Realtime updates
 *
 * Handles:
 * - Starting video generation
 * - Subscribing to realtime status updates
 * - Checking status on page load for pending videos
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { generateVideo, checkGenerationStatus, type GenerateVideoRequest } from "@/lib/api/generateVideo";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { fetchPendingVideos } from "@/services/db/generatedVideos";
import type { PendingVideo } from "@/lib/types/videoTypes";
import { useStaleGenerationCleanup } from "@/hooks/useStaleGenerationCleanup";
import { CONTENT_FLAGGED_CODE } from "@shared/schemas/enums";

export function useVideoGeneration() {
  const { user } = useAuth();
  const { refreshCredits } = useCredits();
  const { t } = useLanguage();
  const [pendingVideos, setPendingVideos] = useState<PendingVideo[]>([]);
  const [isStarting, setIsStarting] = useState(false);

  /**
   * Start a new video generation
   */
  const startGeneration = useCallback(
    async (params: GenerateVideoRequest): Promise<{ success: boolean; error?: string; code?: string }> => {
      if (!user) {
        return { success: false, error: "Not authenticated" };
      }

      setIsStarting(true);

      try {
        const response = await generateVideo(params);

        if (!response.success || !response.videoId) {
          return { success: false, error: response.error, code: response.code };
        }

        // Add to pending videos
        const pendingVideo: PendingVideo = {
          id: response.videoId,
          taskId: response.taskId || "",
          status: "pending",
          prompt: params.prompt,
          createdAt: new Date().toISOString(),
        };

        setPendingVideos((prev) => [pendingVideo, ...prev]);

        return { success: true };
      } catch (error) {
        console.error("Error starting video generation:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      } finally {
        setIsStarting(false);
      }
    },
    [user]
  );

  /**
   * Check status of a specific video
   */
  const checkStatus = useCallback(async (videoId: string) => {
    try {
      await checkGenerationStatus(videoId, "video");
      // The actual update happens via realtime subscription
    } catch (error) {
      console.error("Error checking video status:", error);
    }
  }, []);

  /**
   * Load pending videos from database on mount
   */
  useEffect(() => {
    if (!user) {
      setPendingVideos([]);
      return;
    }

    const loadPendingVideos = async () => {
      try {
        const data = await fetchPendingVideos(user.id);

        if (data.length > 0) {
          const pending: PendingVideo[] = data.map((v) => ({
            id: v.id,
            taskId: v.task_id || "",
            status: v.status as "pending" | "processing",
            prompt: v.prompt,
            createdAt: v.created_at,
          }));

          setPendingVideos(pending);

          // Trigger status checks for all pending videos
          pending.forEach((v) => checkStatus(v.id));
        }
      } catch (error) {
        console.error("Error loading pending videos:", error);
      }
    };

    loadPendingVideos();
  }, [user, checkStatus]);

  /**
   * Subscribe to realtime updates for pending videos
   */
  useEffect(() => {
    if (!user || pendingVideos.length === 0) {
      return;
    }

    const pendingIds = pendingVideos.map((v) => v.id);

    const channel = supabase
      .channel("video-updates")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "generated_videos",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const video = payload.new as {
            id: string;
            status: string;
            video_url?: string;
            error_message?: string;
          };

          // Only process videos we're tracking
          if (!pendingIds.includes(video.id)) {
            return;
          }

          if (video.status === "completed") {
            // Remove from pending
            setPendingVideos((prev) => prev.filter((v) => v.id !== video.id));

            // Show success toast
            toast.success(t.generate.videoReady, {
              description: t.generate.videoReadyDesc,
            });
          } else if (video.status === "failed") {
            // Remove from pending
            setPendingVideos((prev) => prev.filter((v) => v.id !== video.id));

            // Refresh credits (server already refunded)
            refreshCredits();

            // Show content-flagged or generic error toast
            const isFlagged = video.error_message === CONTENT_FLAGGED_CODE;
            toast.error(
              isFlagged ? t.generate.contentFlagged : t.generate.generationFailed,
              {
                description: isFlagged
                  ? t.generate.contentFlaggedDesc
                  : (video.error_message || t.generate.generationFailedWithRefund),
              }
            );
          } else if (video.status === "processing") {
            // Update status to processing
            setPendingVideos((prev) =>
              prev.map((v) => (v.id === video.id ? { ...v, status: "processing" } : v))
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, pendingVideos, t]);

  // Auto-fail stale pending videos
  const handleStaleCleaned = useCallback((ids: string[]) => {
    setPendingVideos((prev) => prev.filter((v) => !ids.includes(v.id)));
  }, []);

  useStaleGenerationCleanup({
    pendingItems: pendingVideos.map((v) => ({ id: v.id, created_at: v.createdAt })),
    table: "generated_videos",
    onCleaned: handleStaleCleaned,
  });

  return {
    pendingVideos,
    pendingCount: pendingVideos.length,
    isStarting,
    startGeneration,
    checkStatus,
  };
}
